import React, { useEffect, useRef, useState } from "react"
import { AirwallexPantokenResponse } from "./hooks/useCreateAirwallexPanToken"
import { useIntl } from "react-intl"
import { useTheme } from "@carrotfertility/carotene-core"
import { AirwallexPinValueLoading } from "./AirwallexPinRow"
import { isAirwallexOrigin } from "./airwallexOrigin"
import Settings from "../../../../../utils/CarrotConfig"

interface AirwallexPinDisplayProps {
  onError: (error: any) => void
  panToken: AirwallexPantokenResponse
}

export const AirwallexPinDisplay: React.FC<AirwallexPinDisplayProps> = ({ onError, panToken }) => {
  const [loaded, setLoaded] = useState(false)
  const [height, setHeight] = useState(0)
  const theme = useTheme()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (!panToken?.card_id) return
    function handleMessage(event: MessageEvent<{ type?: string; error?: unknown; height?: number }>) {
      // Authenticate the sender: right host, and posted by the iframe window we created — not
      // some other frame that happens to sit on an *.airwallex.com origin.
      if (!isAirwallexOrigin(event.origin)) return
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.data?.type === `${panToken.card_id}:pin:loaded`) setLoaded(true)
      if (event.data?.type === `${panToken.card_id}:pin:error`) {
        setLoaded(true)
        onError(new Error(`Error loading card PIN: ${String(event.data.error)}`))
      }
      if (event.data?.type === `${panToken.card_id}:pin:resize` && typeof event.data.height === "number") {
        setHeight(event.data.height)
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [panToken, onError])

  const intl = useIntl()

  if (!panToken?.token) return <AirwallexPinValueLoading />

  // Pass only the short-lived token and presentation rules. PIN digits are rendered
  // and retained inside Airwallex's cross-origin iframe.
  const hash = {
    token: panToken.token,
    rules: {
      ".pin": {
        alignItems: "center",
        backgroundColor: "transparent",
        display: "flex",
        justifyContent: "flex-start",
        margin: "0px",
        padding: "0px"
      },
      ".pin__value": {
        color: theme.palette.text.primary,
        fontSize: String(theme.typography.body2.fontSize),
        fontWeight: String(theme.typography.fontWeightBold),
        margin: "0px",
        padding: "0px"
      }
    }
  }

  const hashURI = encodeURIComponent(JSON.stringify(hash))
  const iframeSrc = `https://${Settings.AIRWALLEX_DOMAIN}/issuing/pci/v2/${panToken?.card_id}/pin#${hashURI}`

  return (
    <>
      {!loaded && <AirwallexPinValueLoading />}
      <iframe
        ref={iframeRef}
        title={intl.formatMessage({
          defaultMessage: "card PIN",
          description: "Accessible title for the secure iframe displaying a member's card PIN"
        })}
        // Until a :pin:resize arrives, hold one line of value text: a bare `${height}px` renders a
        // 0px frame, which reads as "the PIN never loaded" even when it loaded fine.
        style={{
          blockSize: height > 0 ? `${height}px` : theme.spacing(theme.tokens.spacing.lg),
          inlineSize: "100%",
          border: 0,
          display: loaded ? "block" : "none"
        }}
        src={iframeSrc}
      />
    </>
  )
}
